import axios from "@/utils/axios";
import {
  propOr,
  pathOr,
  tap,
  path,
  identity,
  sum,
  last,
  compose,
  length,
  unless,
  isNil,
  map,
  objOf,
  propEq,
  reject,
  invoker,
} from "ramda";
import { NUMBER_ELEMENTS_PER_PAGE } from "@/constants/generic";
import {
  RESET_CLASSES,
  ADD_CLASSES,
  ADD_ALL_CLASSES,
  UPDATE_BUILDING_CLASS,
  RESET_BUILDING_CLASS,
  ADD_CLASS,
  SET_CURRENT_CLASS,
  SET_CURRENT_CLASS_STUDENTS,
  SET_CURRENT_CLASS_ALL_STUDENTS,
  SET_GOALS,
  SET_LESSONS_TOTAL_TIME_PRACTICED,
  SET_LESSONS_TIME_PRACTICED,
  SET_STUDYSET_TIME_PRACTICED,
  SET_AVG_PRACTICE_TIME,
  SET_ACTIVE_STUDENTS,
  SET_EPS,
  SET_LESSONS,
  SET_STUDENT_ACTIVITY,
  SET_OVERVIEW_DATA,
  SET_PREV_OVERVIEW_DATA,
  SET_WEEKLY_ACTIVE_STUDENTS,
  SET_ASSESSMENT_DATA,
  SET_EXPORT_LESSON_DATA,
  RESET_EXPORT_LESSON_DATA,
  SET_PENDING_INVITE_USERS,
  SET_STUDENTS_IMPROVEMENT,
  LOADING_IMPRO_CHART,
  SET_IMPORT_CLASSES_PREVIEW,
  SET_IMPORT_USERS_CLASSES_PREVIEW,
  UPDATE_PENDING_INVITATION,
  SET_EXPORT_ASSESSMENT_DATA,
  RESET_EXPORT_ASSESSMENT_DATA,
  SET_ASSESSMENT_USER,
  SET_EXPORT_LESSON_DETAIL_DATA,
  RESET_EXPORT_LESSON_DETAIL_DATA,
  SET_LESSON_DETAIL_USER,
  SET_AVG_ELSA_SCORE,
  SET_DISTRIBUTION_EPS,
  SET_DISTRIBUTION_IELTS,
  LOADING_CLASSES_TEAM_LEADERBOARD,
  LESS_INDIVIDUAL_LEADERBOARD,
  SET_INDIVIDUAL_LEADERBOARD,
  ADD_INDIVIDUAL_LEADERBOARD,
  SET_PENTAGON,
  SET_AVERAGE_SCORE_SKILLS,
  LOADING_AVERAGE_SCORE_SKILLS,
  SET_LATEST_ASSESSMENTS,
  RESET_LATEST_ASSESSMENTS,
  RESET_ASSESSMENT_USER,
  SET_EXPORT_ASSIGNMENT_DATA,
  RESET_EXPORT_ASSIGNMENT_DATA,
  SET_EXPORT_ASSIGNMENT_DETAIL_DATA,
  SET_EXPORT_ASSIGNMENT_DETAIL,
  RESET_EXPORT_ASSIGNMENT_DETAIL_DATA,
  SAVE_LICENSE_STATE,
  SET_AI_CONVERSATION_REPORT,
  SET_SUM_ACTIVE_STUDENTS,
} from "../constants";
import { TEACHER, STUDENT } from "@/constants/userRoles";
import chartsFragment, {
  getAbstractGetChartData,
  getAbstractGetChartDataWithoutEmptyGuard,
  getAbstractLessonsPracticed,
  getAbstractStudySetsPracticed,
  getAbstractAvgPracticeTime,
} from "./chartsFragment";
import { extractDate } from "@/utils/dateInterval";
import { format, differenceInDays } from "date-fns";
import percentage from "@/components/common/mixins/percentage";
import { round } from "@/utils/ramda-utils";
import { parseISO } from "date-fns";
import subDays from "date-fns/subDays";
import formatISO9075 from "date-fns/formatISO9075";

const { state: chartFragmentState, mutations: chartFragmentMutations, actions: chartFragmentActions } = chartsFragment;
const initialState = () => ({
  // Object that represents that class that is being created
  buildingClass: {},

  // List of all classes for the default organization
  list: [],
  all_classes: [],
  // Selected class
  currentClass: {},

  studentActivity: [],

  assessmentTest: {},

  exportLessonData: [],

  exportAssessmentData: [],

  exportLessonDetailData: [],
  currentLicenseState: [],

  exportAssignmentData: {},
  exportAssignmentDetailData: {},
  exportAssignmentDetail: {},
  pendingInviteUsers: {},

  studentsImprovement: {},
  isLoadingImproChart: false,
  importClassesPreview: [],
  importUsersClassesPreview: [],
  currentClassAllStudent: [],
  assessmentsUser: [],
  lessonsDetailUser: [],
  avgElsaScore: [],
  distributionEps: {},
  distributionIelts: {},
  loadingIndividualLeaderboard: false,
  pentagon: [],
  latestAssessments: [],
  aiConversationReport: {},
  // Attributes for chart data
  ...chartFragmentState,
});

const state = initialState();

const getters = {
  classesSize: compose(length, pathOr([], ["list", "results"])),
  latestAssessmentsSize: compose(length, pathOr([], ["latestAssessments", "results"])),
  getBuildingClass: propOr({}, "buildingClass"),
  getClassList: pathOr([], ["list", "results"]),
  getAllClasses: pathOr([], ["all_classes"]),
  getLatestAssessments: pathOr([], ["latestAssessments", "results"]),
  getCurrentClass: propOr({}, "currentClass"),
  getClassOverview: pathOr({}, ["currentClass", "lessons"]),
  buildPagination(state) {
    const pageCounter = getters.classesSize(state);
    const numberPages = state.list.count / NUMBER_ELEMENTS_PER_PAGE;
    return {
      pageCounter,
      total: state.list.count || 0,
      next: state.list.next,
      previous: state.list.previous,
      numberPages: Math.floor(numberPages),
    };
  },
  buildPaginationLatestAssessment(state) {
    const pageCounter = getters.latestAssessmentsSize(state);
    const numberPages = state.latestAssessments.count / NUMBER_ELEMENTS_PER_PAGE;
    return {
      pageCounter,
      total: state.latestAssessments.count || 0,
      next: state.latestAssessments.next,
      previous: state.latestAssessments.previous,
      numberPages: Math.floor(numberPages),
    };
  },
  getCurrentClassId: path(["currentClass", "id"]),
  pendingUsersCount: pathOr(0, ["pendingInviteUsers", "count"]),
  pendingInviteUsersPage: pathOr([], ["pendingInviteUsers", "results"]),
  getCurrentClassAllStudent: propOr({}, "currentClassAllStudent"),
  getClassGoas: pathOr({}, "set_goals"),
};

const mutations = {
  [ADD_CLASSES](state, classes) {
    state.list = classes;
  },
  [ADD_ALL_CLASSES](state, classes) {
    state.all_classes = classes;
  },
  [UPDATE_BUILDING_CLASS](state, updatedClass) {
    state.buildingClass = {
      ...state.buildingClass,
      ...updatedClass,
    };
  },
  [RESET_BUILDING_CLASS](state) {
    state.buildingClass = {};
  },
  [ADD_CLASS](state, classObject) {
    state.list = [...state.list, classObject];
  },
  [SET_CURRENT_CLASS](state, currentClass) {
    state.currentClass = {
      ...state.currentClass,
      ...currentClass,
    };
  },
  [SET_CURRENT_CLASS_STUDENTS](state, students) {
    state.currentClass = {
      ...state.currentClass,
      users_count: students.count,
      students,
    };
  },
  [SET_CURRENT_CLASS_ALL_STUDENTS](state, students) {
    state.currentClassAllStudent = students;
  },
  [RESET_CLASSES](state) {
    Object.assign(state, initialState());
  },
  [SET_GOALS](state, goals) {
    state.currentClass = { ...state.currentClass, goals };
  },
  [UPDATE_PENDING_INVITATION](state, pending_invitations) {
    state.currentClass = { ...state.currentClass, pending_invitations };
  },
  [SET_LESSONS](state, lessons) {
    state.currentClass = { ...state.currentClass, lessons };
  },
  [SET_ACTIVE_STUDENTS](state, activeStudents) {
    state.activeStudents = activeStudents;
  },
  [SET_STUDENT_ACTIVITY](state, studentActivity) {
    studentActivity.map((item, idx) => {
      item.data.map((time, index) => {
        studentActivity[idx].data[index] = (time / 60).toFixed(0);
      });
    });
    state.studentActivity = studentActivity;
  },
  [SET_OVERVIEW_DATA](state, overviewData) {
    state.overviewData = overviewData;
  },
  [SET_SUM_ACTIVE_STUDENTS](state, sumActiveStudents) {
		state.sumActiveStudents = sumActiveStudents;
	},
  [SET_PREV_OVERVIEW_DATA](state, prevOverviewData) {
    const numberOfLessons = sum(prevOverviewData.numberOfLessons);
		const numberOfMinutes = sum(prevOverviewData.numberOfMinutes);
		let previewData = {
			"lessons_count": numberOfLessons,
			"hours": numberOfMinutes / 60,
		};
    state.prevOverviewData = {...previewData};
  },
  [SET_ASSESSMENT_DATA](state, assessmentTest) {
    state.assessmentData = assessmentTest;
  },
  [SET_EXPORT_LESSON_DATA](state, exportData) {
    // clear eps if no learning lesson in period
    if (exportData["lession_practice"] == 0) {
      exportData["Last_EPS"] = null;
      exportData["First_EPS"] = null;
    }

    exportData["assessment_score_change"] = parseInt(exportData["assessment_test_eps"]) - parseInt(exportData["first_assessment_test_eps"]);
    exportData["assessment_score_change"] = percentage.filters.toPercentage(pathOr(null, ["assessment_score_change"])(exportData));
    exportData["eps_improvement"] = percentage.filters.toPercentage(
      pathOr(null, [])(parseInt(exportData["Last_EPS"]) - parseInt(exportData["First_EPS"]))
    );
    state.exportLessonData = [...state.exportLessonData, exportData];
  },
  [RESET_EXPORT_LESSON_DATA](state) {
    state.exportLessonData = [];
  },
  [SET_EXPORT_ASSIGNMENT_DATA](state, exportData) {
    state.exportAssignmentData = { ...state.exportAssignmentData, ...exportData };
  },
  [RESET_EXPORT_ASSIGNMENT_DATA](state) {
    state.exportAssignmentData = {};
  },
  [SET_EXPORT_ASSIGNMENT_DETAIL_DATA](state, exportData) {
    state.exportAssignmentDetailData = { ...state.exportAssignmentDetailData, ...exportData };
  },
  [SET_EXPORT_ASSIGNMENT_DETAIL](state, exportData) {
    state.exportAssignmentDetail = { ...state.exportAssignmentDetail, ...exportData };
  },
  [RESET_EXPORT_ASSIGNMENT_DETAIL_DATA](state) {
    state.exportAssignmentDetailData = {};
    state.exportAssignmentDetail = [];
  },
  [SET_EXPORT_ASSESSMENT_DATA](state, exportData) {
    state.exportAssessmentData = [...state.exportAssessmentData, ...exportData];
  },
  [RESET_EXPORT_ASSESSMENT_DATA](state) {
    state.exportAssessmentData = [];
  },
  [SET_EXPORT_LESSON_DETAIL_DATA](state, exportData) {
    state.exportLessonDetailData = [...state.exportLessonDetailData, ...exportData];
  },
  [RESET_EXPORT_LESSON_DETAIL_DATA](state) {
    state.exportLessonDetailData = [];
  },
  [SET_ASSESSMENT_USER](state, assessments) {
    const assessmentDataAdjust = [];
    assessments.forEach(assessment => {
      const assessment_test_eps = assessment["eps"] ? assessment["eps"] : assessment["overall_score"];
      const newAssessment = {
        ...assessment,
        assessment_test_eps: assessment_test_eps,
      };
      assessmentDataAdjust.push(newAssessment);
    });
    state.assessmentsUser = assessmentDataAdjust;
  },
  [RESET_ASSESSMENT_USER](state) {
    state.assessmentsUser = [];
  },
  [SET_LESSON_DETAIL_USER](state, lessons) {
    state.lessonsDetailUser = lessons;
  },
  [SET_PENDING_INVITE_USERS](state, pendingInviteUsers) {
    state.pendingInviteUsers = pendingInviteUsers;
  },
  [SET_STUDENTS_IMPROVEMENT](state, studentsImprovement) {
    state.studentsImprovement = studentsImprovement;
  },
  [LOADING_IMPRO_CHART](state, loading) {
    state.isLoadingImproChart = loading;
  },
  [SET_AVG_ELSA_SCORE](state, avgElsaScore) {
    state.avgElsaScore = avgElsaScore;
  },
  [SET_DISTRIBUTION_EPS](state, distributionEps) {
    state.distributionEps = distributionEps;
  },
  [SET_DISTRIBUTION_IELTS](state, distributionIelts) {
    state.distributionIelts = distributionIelts;
  },
  [SET_IMPORT_CLASSES_PREVIEW](state, importClassesPreview) {
    state.importClassesPreview = importClassesPreview;
  },
  [SET_IMPORT_USERS_CLASSES_PREVIEW](state, importUsersClassesPreview) {
    state.importUsersClassesPreview = importUsersClassesPreview;
  },
  [SET_PENTAGON](state, pentagon) {
    state.pentagon = pentagon;
  },
  ...chartFragmentMutations,
  [LOADING_CLASSES_TEAM_LEADERBOARD](state, loading) {
    state.loadingIndividualLeaderboard = loading;
  },
  [LOADING_AVERAGE_SCORE_SKILLS](state, loading) {
    state.loadingAverageScoreSkills = loading;
  },
  [SET_LATEST_ASSESSMENTS](state, latestAssessments) {
    state.latestAssessments = latestAssessments;
  },
  [RESET_LATEST_ASSESSMENTS](state, latestAssessments) {
    state.latestAssessments = [];
  },
  [SAVE_LICENSE_STATE](state, currentLicenseState) {
    state.currentLicenseState = JSON.parse(JSON.stringify(currentLicenseState));
  },
  [SET_AI_CONVERSATION_REPORT](state, aiConversationReport) {
    state.aiConversationReport = aiConversationReport;
  },
};

const actions = {
  updateBuildingClass({ commit }, updatedClass) {
    commit(UPDATE_BUILDING_CLASS, updatedClass);
  },
  createClass({ commit, rootGetters }, classObject) {
    const loggedInUser = rootGetters["user/getUser"];
    const isTeacherAvailable = !!classObject.teacher;
    const requestBody = {
      name: classObject.className,
      start_date: classObject.startDate ? new Date(classObject.startDate) : null,
      owner: isTeacherAvailable ? null : loggedInUser.email,
      end_date: classObject.endDate ? new Date(classObject.endDate) : null,
      is_draft: classObject.is_draft,
    };
    const students = propOr([], ["users"])(classObject);
    const studentsEmails = students.map(student => student.text);
    const studentsFirstNames = students.map(student => student.first_name);
    const studentsLastName = students.map(student => student.last_name);
    const packages = students.map(student => (student.package ? student.package : null));
    const plan_ids = students.map(student => (student.plan_id ? student.plan_id : null));
    const organizationId = rootGetters["organization/currentOrganizationId"];
    const teacher = classObject.teacher || loggedInUser.email;
    const teacher_first_name = classObject.teacher_first_name || loggedInUser.first_name;
    const teacher_last_name = classObject.teacher_last_name || loggedInUser.last_name;
    const isDraft = classObject.is_draft;
    const consent = classObject.consent;

    return axios.post(`/v1/groups/${organizationId}/`, requestBody).then(async response => {
      const createdClass = response.data;
      setClassGoal(createdClass.id, classObject);

      if (!isDraft) {
        if (isTeacherAvailable) {
          await handleInviteTeacher(createdClass, {
            email: teacher,
            first_name: teacher_first_name,
            last_name: teacher_last_name,
          });
        }

        if (studentsEmails.length > 0) {
          await handleInviteStudent(
            studentsEmails,
            studentsFirstNames,
            studentsLastName,
            packages,
            plan_ids,
            { ...createdClass, consent },
            commit
          );
        }
      }

      return response;
    });
  },
  resetBuildingClass({ commit }) {
    commit(RESET_BUILDING_CLASS);
  },
  getClasses({ commit, rootGetters }, params) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    return axios.get(`/v1/groups/${organizationId}/`, { params }).then(response => commit(ADD_CLASSES, response.data));
  },
  getAllClasses({ commit, rootGetters }, params) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    return axios
      .get(`/v1/organizations/${organizationId}/all-groups`, { params })
      .then(response => commit(ADD_ALL_CLASSES, response.data));
  },
  getClass({ dispatch }, { organizationId, groupId } = {}) {
    return axios
      .get(`/v1/groups/${organizationId}/${groupId}/`)
      .then(tap(response => dispatch("setCurrentClass", response.data)));
  },
  setCurrentClass({ commit }, currentClass) {
    return commit(SET_CURRENT_CLASS, currentClass);
  },
  updateClass({ commit, getters }) {
    const updatedClass = getters["getBuildingClass"];
    const originalTeacher = pathOr("", ["getCurrentClass", "owner", "email"])(getters);
    const newTeacher = pathOr("", ["getBuildingClass", "teacher"])(getters);
    const inviteNewTeacher = originalTeacher !== newTeacher;
    const owner = inviteNewTeacher ? null : updatedClass.teacher;
    const inviteNewTeacherPromise = inviteNewTeacher
      ? handleInviteNewTeacher({
          email: updatedClass.teacher,
          first_name: updatedClass.teacher_first_name,
          last_name: updatedClass.teacher_last_name,
        })
      : identity;

    return axios
      .put(`/v1/groups/${updatedClass.organization}/${updatedClass.id}/`, {
        name: updatedClass.className,
        start_date: updatedClass.startDate ? new Date(updatedClass.startDate) : null,
        end_date: updatedClass.endDate ? new Date(updatedClass.endDate) : null,
        owner,
        is_draft: false,
      })
      .then(tap(inviteNewTeacherPromise))
      .then(response => {
        setClassGoal(updatedClass.id, updatedClass);

        const newClass = {
          ...response.data,
          goals: {
            practice_days_per_week: updatedClass.daysPractice,
            practice_minutes_per_day: updatedClass.minutesPractice,
            test_before_class_stats: updatedClass.takeAssessment ? updatedClass.engagementDays : null,
          },
        };

        commit(SET_CURRENT_CLASS, newClass);
        commit(RESET_BUILDING_CLASS);
      });
  },
  addUsersToClass(_, { groupId, students = [] } = {}) {
    return axios.post(`/v1/groups/${groupId}/users`, {
      students,
    });
  },
  removeUserFromClass(_, { groupId, studentId } = {}) {
    return axios.delete(`/v1/groups/${groupId}/users/${studentId}`);
  },
  removeTeacherFromClass({ _, rootGetters }, { groupId, groupName } = {}) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    return axios.put(`/v1/groups/${organizationId}/${groupId}/`, {
      name: groupName,
      owner: null,
    });
  },
  assignTeacherToClass({ _, rootGetters }, { groupId, groupName, email } = {}) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    return axios.put(`/v1/groups/${organizationId}/${groupId}/`, {
      name: groupName,
      owner: email,
    });
  },
  deleteClass(_, { organizationId, groupId } = {}) {
    return axios.delete(`/v1/groups/${organizationId}/${groupId}/`);
  },
  assignStudentLicense({ _, rootGetters }, { studentId, plan }) {
    const organizationId = rootGetters["organization/currentOrganizationId"];
    return axios.get(`/v1/organizations/${organizationId}/${studentId}/assign-license`, { params: { plan_id: plan } });
  },
  getCurrentClassUsers(
    { commit, getters },
    { from = Date.now(), to = Date.now(), page = 1, search = null, classId = null }
  ) {
    const params = {
      from: extractDate(from),
      to: extractDate(to),
      search,
      page,
    };
    const currentClass = classId || getters.getCurrentClassId;
    return axios
      .get(`/v1/groups/${currentClass}/users`, { params })
      .then(response => {
        commit(SET_CURRENT_CLASS_STUDENTS, response.data);
        return { status: true, data: response.data };
      })
      .catch(() => {
        return { status: false, data: null };
      });
  },
  getCurrentClassUsersEditPage({ commit, getters }) {
    const currentClass = getters.getCurrentClassId;
    return axios
      .get(`/v1/groups/${currentClass}/users/all`)
      .then(response => {
        commit(SET_CURRENT_CLASS_STUDENTS, response.data);
        return { status: true, data: response.data };
      })
      .catch(() => {
        return { status: false, data: null };
      });
  },
  getCurrentClassAllUsers({ commit, rootGetters }, params) {
    const currentClass = rootGetters["classes/getCurrentClass"];
    return axios.get(`/v1/groups/${currentClass.id}/all-users`, { params }).then(response => {
      commit(SET_CURRENT_CLASS_ALL_STUDENTS, response.data);
    });
  },
  async getClassGoals({ commit }, classId) {
    try {
      const result = await axios.get(`/v1/assignments/${classId}/`);
      commit(SET_GOALS, result.data);
    } catch (error) {
      error;
    }
  },
  reset({ commit }) {
    return commit(RESET_CLASSES);
  },
  reset_latest_assessments({ commit }) {
    return commit(RESET_LATEST_ASSESSMENTS);
  },
  resetExportLessonData({ commit }) {
    return commit(RESET_EXPORT_LESSON_DATA);
  },
  resetExportAssessmentData({ commit }) {
    return commit(RESET_EXPORT_ASSESSMENT_DATA);
  },
  resetExportLessonDetailData({ commit }) {
    return commit(RESET_EXPORT_LESSON_DETAIL_DATA);
  },
  resetExportAssignmentData({ commit }) {
    return commit(RESET_EXPORT_ASSIGNMENT_DATA);
  },
  resetExportAssignmentDetailData({ commit }) {
    return commit(RESET_EXPORT_ASSIGNMENT_DETAIL_DATA);
  },
  async getChartDataNoPeriod({ dispatch, getters }, { groupId: _groupId = null } = {}) {
    const groupId = _groupId || getters.getCurrentClass.id;
    const options = {
      groupId,
    };
    await Promise.all([dispatch("getDistributionEps", options), dispatch("getAvgElsaScore", options)]);
    await Promise.all([dispatch("getPentagon", options), dispatch("getDistributionIelts", options)]);
  },
  async getChartDataPeriod(
    { dispatch, getters },
    { to = Date.now(), from = Date.now(), groupId: _groupId = null, scope } = {}
  ) {
    const groupId = _groupId || getters.getCurrentClass.id;
    const options = {
      groupId,
      from: extractDate(from),
      to: extractDate(to),
      scope,
    };
    await Promise.all([
      // dispatch("getLessonsOverview", options),
      dispatch("getLessonsTotalTimePracticed", options),
      dispatch("getAvgPracticeTime", options),
      dispatch("getWeeklyActiveStudents", options),
      dispatch("getSumActiveStudents", options),
    ]);
    // await Promise.all([
    // 	dispatch('getAvgPracticeLessons', options),
    // 	dispatch('getPracticeTime', options),
    // ]);
    await Promise.all([dispatch("getActiveStudents", options), dispatch("getEPS", options)]);
    Promise.all([
      dispatch("getPrevLessonsOverview", options),
      // dispatch('getAssignmentLessons', options),
      // dispatch('getAssignmentStudysets', options),
      dispatch("getAverageScoreSkills", options),
      dispatch("getStudentsImprovement", options),
    ]);
  },
  getLessonsTotalTimePracticed({ commit }, { groupId, from, to, scope } = {}) {
    const url = `/v1/reports/groups/${groupId}/lessons_practiced`;
    return getAbstractLessonsPracticed({
      axios,
      commit,
      mutationId: SET_LESSONS_TOTAL_TIME_PRACTICED,
      url,
      from,
      to,
      scope,
    });
  },
  getAssignmentLessons({ commit }, { groupId, from, to, scope } = {}) {
    const url = `/v1/reports/groups/${groupId}/assignment_lessons`;
    return getAbstractLessonsPracticed({
      axios,
      commit,
      mutationId: SET_LESSONS_TIME_PRACTICED,
      url,
      from,
      to,
      scope,
    });
  },
  getAssignmentStudysets({ commit }, { groupId, from, to, scope } = {}) {
    const url = `/v1/reports/groups/${groupId}/assignment_studysets`;
    return getAbstractStudySetsPracticed({
      axios,
      commit,
      mutationId: SET_STUDYSET_TIME_PRACTICED,
      url,
      from,
      to,
      scope,
    });
  },
  getAvgPracticeTime({ commit }, { groupId, from, to, scope } = {}) {
    const url = `/v1/reports/groups/${groupId}/avg_practice_time`;
    return getAbstractAvgPracticeTime({
      axios,
      commit,
      url,
      mutationId: SET_AVG_PRACTICE_TIME,
      from,
      to,
      name: "avg_practice_time",
      scope,
    });
  },
  getActiveStudents({ commit }, { groupId, from, to, scope } = {}) {
    const url = `/v1/reports/groups/${groupId}/active_students`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_ACTIVE_STUDENTS,
      from,
      to,
      name: "active_students",
      scope,
    });
  },
  getEPS({ commit }, { groupId, from, to, scope } = {}) {
    const url = `/v1/reports/group/${groupId}/eps`;
    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_EPS,
      from,
      to,
      name: "eps",
      scope,
    });
  },
  getAvgElsaScore({ commit }, { groupId } = {}) {
    const url = `v1/reports/groups/${groupId}/avg_elsa_score`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_AVG_ELSA_SCORE,
      name: "avg_elsa_score",
    });
  },
  getDistributionEps({ commit }, { groupId, scope } = {}) {
    const url = `/v1/reports/groups/${groupId}/distribution_eps`;
    return getAbstractGetChartDataWithoutEmptyGuard({
      axios,
      commit,
      url,
      mutationId: SET_DISTRIBUTION_EPS,
      name: "distribution_eps",
    });
  },
  getDistributionIelts({ commit }, { groupId, scope } = {}) {
    const url = `/v1/reports/groups/${groupId}/distribution_ielts`;
    return getAbstractGetChartDataWithoutEmptyGuard({
      axios,
      commit,
      url,
      mutationId: SET_DISTRIBUTION_IELTS,
      name: "distribution_ielts",
    });
  },
  getPentagon({ commit }, { groupId } = {}) {
    const url = `v1/reports/groups/${groupId}/pentagon`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_PENTAGON,
      name: "pentagon",
    });
  },
  getStudentsImprovement({ commit }, { groupId, from, to, scope } = {}) {
    const url = `v1/reports/groups/${groupId}/improvement`;
    commit(LOADING_IMPRO_CHART, true);
    return axios.get(url, { params: { scope, from, to } }).then(response => {
      commit(SET_STUDENTS_IMPROVEMENT, response.data);
      commit(LOADING_IMPRO_CHART, false);
    });
  },
  getLessonsOverview({ commit }, { groupId, from, to, scope }) {
    const url = `/v1/reports/group/${groupId}/overview`;
    const params = {
      from,
      to,
      scope,
    };

    return axios.get(url, { params }).then(response => commit(SET_OVERVIEW_DATA, response.data));
  },
  getSumActiveStudents({ commit }, { groupId, from, to, scope }) {
    const url = `/v1/reports/groups/${groupId}/sum_active_users`;
    return axios.get(url, { params: { from, to, scope } }).then(response => commit(SET_SUM_ACTIVE_STUDENTS, response.data));
  },
  getPrevLessonsOverview({ commit }, { groupId, from, to, scope }) {
    const url = `/v1/reports/groups/${groupId}/lessons_practiced`;
    let tempFrom = parseISO(from);
    let tempTo = parseISO(to);
    let previousTo = subDays(tempFrom, 1);
    let previousFrom = subDays(previousTo, differenceInDays(tempTo, tempFrom));

    return getAbstractLessonsPracticed({
      axios,
      commit,
      mutationId: SET_PREV_OVERVIEW_DATA,
      url,
      from: formatISO9075(previousFrom, { representation: "date" }),
      to: formatISO9075(previousTo, { representation: "date" }),
      scope,
    });
  },
  getStudentActivity({ commit }, { groupId, from, to, scope } = {}) {
    const params = {
      from: extractDate(from),
      to: extractDate(to),
      scope,
    };
    return axios
      .get(`/v1/reports/groups/${groupId}/student_time_activity`, { params })
      .then(response => commit(SET_STUDENT_ACTIVITY, response.data));
  },
  getWeeklyActiveStudents({ commit }, { groupId, from, to } = {}) {
    //const url = `/v1/reports/groups/${groupId}/active_students`
    const url = `/v1/reports/groups/${groupId}/weekly_active_students`;

    return getAbstractGetChartData({
      axios,
      commit,
      url,
      mutationId: SET_WEEKLY_ACTIVE_STUDENTS,
      from,
      to,
      name: "weekly_active_students",
      scope: "weekly",
    });
  },
  getAssessmentTest({ commit, getters }, { groupId: _groupId = null, scope, from, to }) {
    const groupId = _groupId || getters.getCurrentClass.id;
    const params = {
      from,
      to,
      scope,
    };

    return axios
      .get(`v1/reports/group/${groupId}/assessment_test`, { params })
      .then(response => commit(SET_ASSESSMENT_DATA, response.data));
  },
  getExportLessonData({ commit, getters }, { student: _student = null, scope, from, to, assessment_type } = {}) {
    const student = _student || getters.currentStudent;
    const studentId = student["id"];
    const params = {
      from: extractDate(from),
      to: extractDate(to),
      scope,
    };
    const studentData = {
      studentId: studentId,
      // 'last_active': student.last_active ? format(new Date(student.last_active), 'HH:mma MM/dd/yyyy') : '',
      student_name: student.first_name + " " + student.last_name,
      student_email: student.email,
    };
    const getLessonsPracticed = axios
      .get(`v1/reports/user/${studentId}/lessons_practiced`, { params })
      .then(response => {
        const total_time_sec = sum(response.data[0].data);
        const hours = Math.floor(total_time_sec / (60 * 60));
        const minutes = Math.floor((total_time_sec - hours * 3600) / 60);
        const seconds = total_time_sec - hours * 3600 - minutes * 60;
        const dateDiff = differenceInDays(to, from) + 1;
        studentData["lession_practice"] = sum(response.data[1].data);
        studentData["total_time"] = `="${hours}:${minutes}:${seconds}"`;
        studentData["number_lesson_practice_perday"] = (studentData["lession_practice"] / dateDiff).toFixed(1);
        studentData["number_of_minutes_perDay"] = (total_time_sec / (60 * dateDiff)).toFixed(0);
        studentData["days_practice"] = response.data[0].data.filter(time => time > 0).length;
        if (studentData["days_practice"] > 0) {
          studentData["learner_engagement_rate"] = percentage.filters.toPercentage(Math.round(studentData["days_practice"] / dateDiff * 100));
          studentData["number_of_minutes_per_active_day"] = (total_time_sec / (60 * studentData["days_practice"])).toFixed(0);
          studentData["number_lesson_practice_per_active_day"] = (studentData["lession_practice"] / studentData["days_practice"]).toFixed(1);
        }
      });

    const getEPS = axios.get(`v1/reports/user/${studentId}/eps_v2`, { params }).then(response => {
      // const data = response.data

      // let cumulativeEps = 0
      // const normalizedEps = map((epsValue) => {
      //   if(!epsValue) return cumulativeEps

      //   return cumulativeEps = epsValue
      // })(data)
      //const epsImprovement = (last(normalizedEps)/(head(normalizedEps) || 1)) * 100
      studentData["Last_EPS"] = percentage.filters.toPercentage(
        compose(unless(isNil, Math.round), pathOr(null, ["data", "last_eps"]))(response)
      );
      studentData["First_EPS"] = percentage.filters.toPercentage(
        compose(unless(isNil, Math.round), pathOr(null, ["data", "first_eps"]))(response)
      );
      //studentData['EPS_Percentage_Improvement'] = compose(concat(__, '%'), toString, round)(epsImprovement)
    });
    const params_assessment = {
      from: extractDate(from),
      to: extractDate(to),
      assessment_type,
    };
    const getAssessment = axios
      .get(`v1/reports/user/${studentId}/assessment_test`, { params: params_assessment })
      .then(response => {
        const ats = response.data;
        if (ats.length > 1) {
          const first_at = ats[0];
          const {assessment_test_eps: first_assessment_test_eps, date_assessment_test: first_date_assessment_test} = process_assessment_test(first_at);
          studentData["first_assessment_test_eps"] = first_assessment_test_eps
          studentData["first_date_assessment_test"] = first_date_assessment_test
          const last_at = ats[1];
          const {assessment_test_eps: last_assessment_test_eps, date_assessment_test: last_date_assessment_test} = process_assessment_test(last_at);
          studentData["assessment_test_eps"] = last_assessment_test_eps
          studentData["last_date_assessment_test"] = last_date_assessment_test
        } else if (ats.length == 1) {
          const last_at = ats[0];
          const {assessment_test_eps, date_assessment_test} = process_assessment_test(last_at);
          studentData["assessment_test_eps"] = assessment_test_eps
          studentData["last_date_assessment_test"] = date_assessment_test
        } else {
          studentData["assessment_test_eps"] = null;
          studentData["last_date_assessment_test"] = "";
        }
      });

    const getLastDatePractice = axios.get(`v1/reports/user/${studentId}/last_active`, { params }).then(response => {
      const last_date_practice = pathOr(null, ["data", "last_active"])(response);
      studentData["last_active"] = last_date_practice ? format(new Date(last_date_practice), "hh:mma dd MMM yyyy") : "";
    });

    const process_assessment_test = (assessment_test) => {
      let assessment_test_eps = null;
      let date_assessment_test = null;
      if (assessment_test.additional_assessment_test) {
        assessment_test_eps = pathOr(null, ["additional_assessment_test"])(assessment_test);
      } else {
        assessment_test_eps = percentage.filters.toPercentage(
          compose(unless(isNil, Math.round), pathOr(null, ["assessment_test"]))(assessment_test)
        );
      }
      const last_date_assessment_test = pathOr(null, ["date"])(assessment_test);
      date_assessment_test = last_date_assessment_test
        ? format(new Date(last_date_assessment_test), "hh:mma dd MMM yyyy")
        : "";

      return { assessment_test_eps, date_assessment_test };
    };

    return Promise.all([getLessonsPracticed, getEPS, getAssessment, getLastDatePractice]).then(() =>
      commit(SET_EXPORT_LESSON_DATA, studentData)
    );
  },
  getExportAssessmentData({ commit, getters }, { student: _student = null, from, to, assessment_type } = {}) {
    const student = _student || getters.currentStudent;
    const studentId = student["id"];
    const params = {
      from: extractDate(from),
      to: extractDate(to),
      assessment_type: assessment_type,
    };
    let studentData = {
      studentId: studentId,
      student_name: student.first_name + " " + student.last_name,
      student_email: student.email,
    };
    const assessmentsData = [];
    return axios.get(`v1/reports/user/${studentId}/assessment_history`, { params }).then(response => {
      if (response.data) {
        response.data.forEach(assessment => {
          const date_assessment = assessment["date_assessment"]
            ? format(new Date(assessment["date_assessment"]), "hh:mma dd MMM yyyy")
            : "";
          if (assessment_type == "sgd-assessment") {
            studentData = {
              ...studentData,
              ...assessment,
              date_assessment: date_assessment,
            };
            assessmentsData.push(studentData);
          } else if (assessment_type == "general") {
            const assessment_test_eps = assessment["eps"]
              ? Math.round(assessment["eps"])
              : Math.round(assessment["overall_score"]);
            studentData = {
              ...studentData,
              ...assessment,
              assessment_test_eps: assessment_test_eps,
              date_assessment: date_assessment,
            };
            for (let i = 0; i < assessment["skills"].length; i++) {
              const skill = assessment["skills"][i];
              let record = {};
              if (i == 0) {
                record = { ...studentData };
              }
              record = {
                ...record,
                skill: skill["name"],
                score_skill: Math.round(skill["score"]),
              };
              assessmentsData.push(record);
            }
          } else {
            const assessment_test_eps = assessment["eps"]
              ? Math.round(assessment["eps"])
              : Math.round(assessment["overall_score"]);
            studentData = {
              ...studentData,
              ...assessment,
              assessment_test_eps: assessment_test_eps,
              date_assessment: date_assessment,
            };
            assessmentsData.push(studentData);
          }
        });
        commit(SET_EXPORT_ASSESSMENT_DATA, assessmentsData);
      }
    });
  },
  getExportLessonDetailData({ commit, getters }, { student: _student = null, from, to } = {}) {
    const student = _student || getters.currentStudent;
    const studentId = student["id"];
    const params = {
      from: extractDate(from),
      to: extractDate(to),
    };
    let studentData = {
      studentId: studentId,
      student_name: student.first_name + " " + student.last_name,
      student_email: student.email,
    };
    let lessonDetailData = [];
    return axios.get(`v1/reports/user/${studentId}/lesson_detail`, { params }).then(response => {
      if (response.data) {
        response.data.forEach(lesson => {
          const date_lesson = lesson["date"] ? format(new Date(lesson["date"]), "hh:mma MM/dd/yyyy") : "";
          const lesson_eps = lesson["overall_native_score"] ? Math.round(lesson["overall_native_score"]) : "";
          const record = {
            ...lesson,
            overall_native_score: lesson_eps,
            date: date_lesson,
            ...studentData,
          };
          lessonDetailData.push(record);
        });
        commit(SET_EXPORT_LESSON_DETAIL_DATA, lessonDetailData);
      }
    });
  },
  getExportOverallAssignmentData({ commit, getters }, { groupId: _groupId = null, assignment } = {}) {
    const groupId = _groupId || getters.getCurrentClassId;
    let assignmentData = {};
    return axios
      .get(`v1/reports/groups/${groupId}/assignments/${assignment.id}/assignment_overall_export`)
      .then(response => {
        if (response.data) {
          let new_resp = response.data;
          assignmentData[assignment.id] = new_resp;
          commit(SET_EXPORT_ASSIGNMENT_DATA, assignmentData);
        }
      });
  },
  getExportDetailAssignmentData({ commit, getters }, { groupId: _groupId = null, assignment } = {}) {
    const groupId = _groupId || getters.getCurrentClassId;
    let assignmentData = {};
    let assignmentDetail = {};
    return axios
      .get(`v1/reports/groups/${groupId}/assignments/${assignment.id}/assignment_detail_export`)
      .then(response => {
        if (response.data) {
          let detail = response.data["assignment_detail"];
          assignmentData[assignment.id] = response.data["assignment_result"].map(item => {
            let detailIdsResult = item["detail"].map(el => el.id);
            let detailFilter = detail.filter(obj => !detailIdsResult.includes(obj.id));
            return { ...item, detail: [...item["detail"], ...detailFilter] };
          });
          assignmentDetail[assignment.id] = detail;
          commit(SET_EXPORT_ASSIGNMENT_DETAIL_DATA, assignmentData);
          commit(SET_EXPORT_ASSIGNMENT_DETAIL, assignmentDetail);
        }
      });
  },
  getAssessmentUser({ commit, getters }, { student: _student = null, from, to, assessment_type } = {}) {
    const student = _student || getters.currentStudent;
    const studentId = student["id"];
    const params = {
      from: extractDate(from),
      to: extractDate(to),
      assessment_type: assessment_type,
    };
    let assessmentsData = [];
    return axios.get(`v1/reports/user/${studentId}/assessment_history`, { params }).then(response => {
      if (response.data) {
        assessmentsData = response.data;
        commit(SET_ASSESSMENT_USER, assessmentsData);
      }
    });
  },
  resetAssessmentUser({ commit }) {
    return commit(RESET_ASSESSMENT_USER);
  },
  getLessonsDetailUser({ commit, getters }, { student: _student = null, from, to } = {}) {
    const student = _student || getters.currentStudent;
    const studentId = student["id"];
    const params = {
      from: extractDate(from),
      to: extractDate(to),
    };
    let lessonsDetailData = [];
    return axios.get(`v1/reports/user/${studentId}/lesson_detail`, { params }).then(response => {
      if (response.data) {
        lessonsDetailData = response.data;
        commit(SET_LESSON_DETAIL_USER, lessonsDetailData);
      }
    });
  },
  getPendingInviteUsers({ commit, getters }, { groupId: _groupId, page = 1 } = {}) {
    const groupId = _groupId || getters.getCurrentClassId;
    const url = `v1/invitations/group/${groupId}/`;
    return axios.get(url, { params: { page } }).then(response => commit(SET_PENDING_INVITE_USERS, response.data));
  },
  removeInvite({ commit, getters }, inviteId) {
    const url = `v1/invitations/${inviteId}/`;
    return axios.delete(url).then(rep => {
      const count = getters.pendingUsersCount - 1;
      // should be update backend to consisten number pending invitation (include teacher or not)
      const pendingCountClassDetail = getters.getCurrentClass.pending_invitations - 1;
      const pendingInviteUsers = reject(propEq("id", inviteId))(getters.pendingInviteUsersPage);
      commit(UPDATE_PENDING_INVITATION, pendingCountClassDetail);
      commit(SET_PENDING_INVITE_USERS, {
        count: count,
        results: pendingInviteUsers,
      });
    });
  },
  getImportClassesUploadUrl(_, { fileSize, contentType, organizationId }) {
    const params = {
      content_type: contentType,
      file_size: fileSize,
    };

    return axios.get(`/v1/groups/${organizationId}/import`, { params });
  },
  postImportClassCallback(_, { organizationId, callbackId }) {
    return axios.post(`/v1/groups/${organizationId}/import`, {
      file_key: callbackId,
    });
  },
  getImportClassesPreview({ commit }, { organizationId, callbackId } = {}) {
    return axios
      .post(`/v1/groups/${organizationId}/preview`, {
        file_key: callbackId,
      })
      .then(response => {
        let importClassPreview = [];
        importClassPreview = map(buildingGroup(organizationId), response.data);
        commit(SET_IMPORT_CLASSES_PREVIEW, importClassPreview);
      });
  },
  getImportUsersClassesPreview({ commit }, { organizationId, callbackId } = {}) {
    return axios
      .post(`/v1/groups/${organizationId}/users/preview`, {
        file_key: callbackId,
      })
      .then(response => {
        let importUsersClassesPreview = [];
        importUsersClassesPreview = map(buildingGroup(organizationId), response.data);
        commit(SET_IMPORT_USERS_CLASSES_PREVIEW, importUsersClassesPreview);
      });
  },
  ...chartFragmentActions,
  async getIndividualLeaderboard(
    { commit, state },
    {
      groupId: _groupId = null,
      from: _from,
      to: _to,
      individual_sort_by,
      individual_order_by,
      individual_cursor = 0,
      page_size = 5,
      replace = false,
    } = {}
  ) {
    // const organizationId = _organizationId || state.currentOrganizationId || state.currentOrganization.id;
    const groupId = _groupId || state.currentClass.id;
    if (!groupId) {
      return;
    }
    const from = extractDate(_from);
    const to = extractDate(_to);

    const url = `v1/reports/groups/${groupId}/leaderboard`;
    commit(LOADING_CLASSES_TEAM_LEADERBOARD, true);
    try {
      await getAbstractGetChartDataWithoutEmptyGuard({
        axios,
        commit,
        url,
        mutationId: replace ? SET_INDIVIDUAL_LEADERBOARD : ADD_INDIVIDUAL_LEADERBOARD,
        name: "individual_leaderboard",
        from,
        to,
        sort_by: individual_sort_by,
        order_by: individual_order_by,
        page: individual_cursor,
        page_size: page_size,
      });
    } finally {
      commit(LOADING_CLASSES_TEAM_LEADERBOARD, false);
    }
  },
  async getAverageScoreSkills({ commit, state, rootGetters }, { groupId, from, to } = {}) {
    const url = `v1/reports/groups/${groupId}/skill_score`;
    commit(LOADING_AVERAGE_SCORE_SKILLS, true);
    try {
      await getAbstractGetChartDataWithoutEmptyGuard({
        axios,
        commit,
        url,
        mutationId: SET_AVERAGE_SCORE_SKILLS,
        name: "average_score_skills",
        from,
        to,
      });
    } finally {
      commit(LOADING_AVERAGE_SCORE_SKILLS, false);
    }
  },
  lessIndividualLeaderboard({ commit }, { page: page, page_size: page_size } = {}) {
    commit(LESS_INDIVIDUAL_LEADERBOARD, { page: page, page_size: page_size });
  },
  getLatestAssessments({ commit, rootGetters }, { from, to, assessment_type, page, classId } = {}) {
    const currentClassId = classId || rootGetters["classes/getCurrentClassId"];
    const params = {
      from: extractDate(from),
      to: extractDate(to),
      assessment_type,
      page,
    };
    return axios
      .get(`v1/groups/${currentClassId}/users-last-assessment`, { params })
      .then(response => {
        commit(SET_LATEST_ASSESSMENTS, response.data);
      })
      .catch(error => {
        commit(SET_LATEST_ASSESSMENTS, {});
        throw error;
      });
  },
  saveLicenseState({ commit, state, rootGetters }, currentLicenseState) {
    commit(SAVE_LICENSE_STATE, currentLicenseState);
  },
  async saveLicenseToServer({ state }, { id } = {}) {
    const payload = state.currentLicenseState.reduce((result, curr) => {
      if (!curr.valueNewLicense) return result;
      result = [...result, { user_id: curr.id, plan_id: curr.valueNewLicense }];
      return result;
    }, []);
    const result = await axios.post(`v1/organizations/${id}/assign-multi-license`, payload);
    return result;
  },
  setAIConversationReport({ commit }, conversation) {
    commit(SET_AI_CONVERSATION_REPORT, conversation);
  },
};
async function handleInviteTeacher(classObject, { email, first_name, last_name }) {
  const inviteBody = {
    emails: [email],
    first_names: [first_name],
    last_names: [last_name],
    role: TEACHER,
    organization: classObject.organization_id,
    group: classObject.id,
    auto_join: 1,
  };
  try {
    await axios.post("/v2/accounts/invite/", inviteBody);
  } catch (error) {
    throw error;
  }
}

const buildingGroup = organizationId => groupOb => {
  return {
    className: groupOb.name,
    startDate: groupOb.start_date,
    endDate: groupOb.end_date,
    teacher: pathOr("", ["owner", "email"])(groupOb),
    organization: organizationId,
    users: compose(map(objOf("text")), pathOr([], ["students"]))(groupOb),
    goals: {
      daysPractice: pathOr(5, ["assignment", "practice_days_per_week"])(groupOb),
      minutesPractice: pathOr(10, ["assignment", "practice_minutes_per_day"])(groupOb),
      takeAssessment: !!path(["assignment", "test_before_class_stats"])(groupOb),
      engagementDays: pathOr(3, ["assignment", "test_before_class_stats"])(groupOb),
    },
  };
};

const handleInviteNewTeacher = owner => response => {
  const organizationId = path(["data", "organization_id"])(response);
  const groupId = path(["data", "id"])(response);

  return handleInviteTeacher(
    {
      organization_id: organizationId,
      id: groupId,
    },
    owner
  );
};

async function handleInviteStudent(
  studentsEmails,
  studentFirstNames,
  studentLastNames,
  packages,
  plan_ids,
  classObject,
  commit
) {
  const inviteBody = {
    emails: studentsEmails,
    first_names: studentFirstNames,
    last_names: studentLastNames,
    role: STUDENT,
    organization: classObject.organization_id,
    group: classObject.id,
    auto_join: 1,
    packages: packages,
    plan_ids: plan_ids,
    is_flow_invite: 1,
  };
  try {
    await axios.post("/v2/accounts/invite_learner/", inviteBody);
    commit(UPDATE_BUILDING_CLASS, { users_count: studentsEmails.length });
  } catch (error) {
    commit(UPDATE_BUILDING_CLASS, { users_count: -1 });
  }
}

async function setClassGoal(classId, buildingClass) {
  const goals = {
    practice_days_per_week: buildingClass.daysPractice,
    practice_minutes_per_day: buildingClass.minutesPractice,
    test_before_class_stats: buildingClass.takeAssessment ? buildingClass.engagementDays : null,
    date_assessment_test_first: buildingClass.engagementFirstDate ? new Date(buildingClass.engagementFirstDate) : null,
    date_assessment_test_second: buildingClass.engagementSecondDate
      ? new Date(buildingClass.engagementSecondDate)
      : null,
  };
  try {
    await axios.post(`/v1/assignments/${classId}/`, goals);
  } catch (error) {
    error;
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
